import { computed, inject, Injector, Type } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
  withFeature,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { map, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';

import { DocumentTypesSignalState } from '../../types';
// import { AdminService } from '../../services/document-types.service';
import { AdminService } from '@admin/admin.service';
import {
  DOCUMENT_TYPES_INITIAL_STATE,
  DOCUMENT_TYPES_ADMIN_INITIAL_STATE_TOKEN,
} from '../../constants/document-types.constant';
import { ShallowCamelCasePipe } from '@shared/pipes/shallow-camel-case.pipe';
import { GridType } from '@data-access/grid-views';
import {
  LoadGridPayload,
  transformGridDateFormat,
  withGridConfigFeatures,
} from '@data-access/shared';
import { withDocumentTypeEntities } from '../entities/document-types.entities';
import { DocumentTypesData } from '@data-access/configurations/types/document.type';
import { BuildLinkFromValue } from '@wcg/frontend-framework';

/**
 * Signal store for managing submission admin state including workspace sub-types search and filters
 */
export const DOCUMENT_TYPES_SIGNAL_STORE = signalStore(
  withState(() => inject(DOCUMENT_TYPES_ADMIN_INITIAL_STATE_TOKEN)),
  withProps((_store, _adminService = inject(AdminService)) => ({
    _injector: inject(Injector),
    _adminService,
    _shallowCamelCasePipe: new ShallowCamelCasePipe(),
    _buildLinkFromValue: new BuildLinkFromValue(),
  })),
  withFeature(() => withGridConfigFeatures()),
  withFeature(() => withDocumentTypeEntities()),
  withComputed(
    ({
      total,
      isLoading,
      gridType,
      error,
      _documenttypesEntities,
      _injector,
      columnsCopy,
    }) => ({
      $documentTypes: computed(() => {
        const _documentTypes = _documenttypesEntities();
        if (!_documentTypes || _documentTypes.length === 0) {
          return [] as DocumentTypesData[]; // Return an empty array if no data
        }

        return transformGridDateFormat<DocumentTypesData>(
          _injector,
          _documentTypes,
          columnsCopy(),
        );
      }),
      $isLoading: computed(() => isLoading()),
      $error: computed(() => error()),
      $gridType: computed(() => {
        const _gridType: GridType = gridType();
        return _gridType;
      }),
      $totalCount: computed(() => total()),
    }),
  ),
  withMethods(
    ({
      _adminService,
      _shallowCamelCasePipe,
      _buildLinkFromValue,
      ...store
    }) => {
      /**
       * Load document types
       */
      const _loadDocumentTypes = rxMethod<LoadGridPayload>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ params, updateColumns }) =>
            _adminService.getAllDocumentTypesList(params).pipe(
              map((response) => ({
                ...response,
                config: _shallowCamelCasePipe.transform(response.config),
              })),

              tapResponse({
                error: (error: Error) =>
                  patchState(store, {
                    error: error,
                    isLoading: false,
                  }),
                finalize: () =>
                  patchState(store, {
                    isLoading: false,
                  }),
                next: (response) => {
                  patchState(store, {
                    currentStateDestroyed: false,
                    total: response.total,
                  });
                  store.setDocumentTypes(response.data);
                  store.loadConfigurations(
                    response.config,
                    updateColumns,
                    response.data?.[0],
                    _buildLinkFromValue,
                  );
                  store.setGridViews(response.views);
                },
              }),
            ),
          ),
        ),
      );

      return {
        loadDocumentTypes: (params: LoadGridPayload) =>
          _loadDocumentTypes(params),
        /**
         * Reset grid settings
         */
        resetGridSettings: (payload: LoadGridPayload) => {
          _loadDocumentTypes(payload);
          patchState(store, { searchString: '' });
          store.setDefaultGridView();
        },

        /**
         * Reset to initial state
         */
        resetState: () =>
          patchState(store, { ...DOCUMENT_TYPES_INITIAL_STATE }),

        // loadIrisRoutingGroups: () => _getIrisRoutingGroups(),
      };
    },
  ),
) satisfies Type<DocumentTypesSignalState>;
